<template>
  <div class="content">
    <!-- 文件列表 -->
    <div class="file-list">
      <div
        class="file-item"
        v-for="(item, index) in completedFileList"
        :key="index"
        :style="{ '--progress': item.progress + '%' }"
        :data-progress="item.progress"
      >
        <div class="file-icon">
          <img :src="item.icon" alt="" />
        </div>
        <div style="flex: 1">
          <div class="file-name">
            <span>{{ item.name }}</span>
          </div>
          <div class="file-size">{{ item.size }}</div>
        </div>
        <div class="num">{{ item.progress }}<span style="font-size: 14px;">%</span></div>
      </div>
    </div>
  </div>
</template>

<script>
import { createDocument, updataDocument } from '@/api/ai.js'
import { getDocumentEmbedProgress } from '@/api/aiApp.js'
import { dataSet } from '@/data/app.js'
const dataSetId = dataSet.id

export default {
  name: 'DataProcessing',
  props: {
    fileList: {
      type: Array,
      default: () => []
    },
    fileConfigs: {
      type: Array,
      default: () => []
    },
    datasetId: {
      type: String,
      default: ''
    },
    currentFileIndex: {
      type: Number,
      default: 0
    },
    fullApply: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      completedFileList: [],
      // 添加一个新数组，用于存储需要检查进度的文件
      pendingCheckFiles: []
    }
  },
  mounted() {
    // 初始化时就显示文件列表，进度为0%
    this.initFileList()
  },
  methods: {
    // 新增方法：初始化文件列表
    initFileList() {
      // 清空现有列表
      this.completedFileList = []

      // 将传入的fileList转换为completedFileList格式
      if (this.fileList && this.fileList.length > 0) {
        this.fileList.forEach(file => {
          // 复制文件对象并添加进度属性
          const fileWithProgress = { ...file, progress: 0.00 }
          this.completedFileList.push(fileWithProgress)
        })
      }
    },

    // 新增方法：启动进度检查
    startProgressCheck() {
      // 清除可能存在的旧定时器
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
      }

      // 创建新的定时器
      this.progressTimer = setInterval(() => {
        // 如果没有需要检查的文件，可以考虑停止定时器
        if (this.pendingCheckFiles.length === 0) {
          clearInterval(this.progressTimer)
          this.progressTimer = null
          console.log('所有文件处理完成，停止检查')
          return
        }

        // 遍历待检查文件列表
        this.pendingCheckFiles.forEach((item, index) => {
          getDocumentEmbedProgress(
            { datasetId: dataSetId, batch: item.batch },
            {
              'X-App-Id': 'dataset'
            }
          )
            .then(res => {
              console.log('获取文件进度', res)
              if (!res) {
                // 如果res为undefined，标记文件为异常状态
                this.$set(item, 'status', 'error')
                this.$set(item, 'error', '获取进度失败')
                // 从待检查列表中移除
                this.pendingCheckFiles.splice(index, 1)
                return
              }
              if (res.data && res.data.length > 0) {
                const obj = res.data[0]

                // 处理完成的情况
                if (obj.indexing_status === 'completed') {
                  this.$set(item, 'progress', 100)
                  // 从待检查列表中移除此文件
                  this.pendingCheckFiles.splice(index, 1)
                  console.log(`文件 ${item.name} 处理完成，从检查列表中移除`)
                  this.$emit('ok')
                } else if (obj.indexing_status === 'indexing') {
                  // 正在处理的情况
                  item.completed_segments = obj.completed_segments
                  item.total_segments = obj.total_segments
                  item.error = obj.error
                  this.$set(item, 'progress', ((obj.completed_segments / obj.total_segments) * 100).toFixed(2))
                } else if (obj.indexing_status === 'error') {
                  this.$message.error('知识库文档处理失败，请重试')
                }
                // 其他状态不处理

                this.$forceUpdate()
              }
            })
            .catch(err => {
              console.error(`获取文件 ${item.name} 进度失败:`, err)
            })
        })
      }, 2000)
    },
    pushFileList() {
      this.$emit('pushFileList', this.completedFileList)
    },

    // 处理单个文件的上传或更新
    async handleSingleFile(item, config, index) {
      const data = this.prepareDocumentData(config, item.name)
      console.log('准备上传或更新文档:', item)
      try {
        let res
        const formData = new FormData()
        formData.append('file', item.file)
        if (item.isDuplicate) {
          formData.append(
            'data',
            JSON.stringify({
              ...data,
              hasFile: true,
              documentId: item.documentId,
              extendBody: { name: item.name, size: item.size }
            })
          )
          res = await updataDocument(formData)
        } else {
          formData.append('data', JSON.stringify({ ...data, extendBody: { name: item.name, size: item.size } }))
          res = await createDocument(formData)
        }
        console.log('res', res)
        if (res.code === 200) {
          // 更新文件信息
          item.batch = res.data.batch
          item.difyDocumentId = res.data.difyDocumentId

          // 更新完成列表中的对应项
          const completedItem = this.completedFileList.find(f => f.name === item.name)
          if (completedItem) {
            completedItem.batch = res.data.batch
            completedItem.difyDocumentId = res.data.difyDocumentId
            this.pendingCheckFiles.push(completedItem)
          }

          // 如果是最后一个文件，启动进度检查
          if (index === this.fileList.length - 1) {
            this.startProgressCheck()
          }
        } else {
          this.$message.error('文档上传失败，请重试')
        }
      } catch (error) {
        console.error(`处理文件 ${item.name} 失败:`, error)
      }
    },

    // 优化后的createDatasetDoc方法
    async createDatasetDoc() {
      console.log('开始创建文档')

      if (this.datasetId) {
        console.log('编辑数据', {
          fileConfigs: this.fileConfigs,
          fileList: this.fileList,
          currentFileIndex: this.currentFileIndex
        })
        // 编辑模式
        const config = this.fileConfigs[this.currentFileIndex]
        const item = this.fileList[this.currentFileIndex]
        const data = this.prepareDocumentData(config)
        data.documentId = this.$route.query.id
        data.datasetId = this.$route.query.datasetId
        try {
          const formData = new FormData()
          formData.append('data', JSON.stringify({ ...data, hasFile: false, extendBody: { name: item.name, size: item.size } }))
          const res = await updataDocument(formData)
          // if (res.code === 200) {
          //   this.$emit('ok')
          // }
          if (res.code === 200) {
            // 更新文件信息
            item.batch = res.data.batch
            item.difyDocumentId = res.data.difyDocumentId

            // 更新完成列表中的对应项
            const completedItem = this.completedFileList.find(f => f.name === item.name)
            if (completedItem) {
              completedItem.batch = res.data.batch
              completedItem.difyDocumentId = res.data.difyDocumentId
              this.pendingCheckFiles.push(completedItem)
            }

            // 如果是最后一个文件，启动进度检查
            this.startProgressCheck()
          }
        } catch (error) {
          console.error('更新文档失败:', error)
        }
        return
      }

      // 处理文件列表
      const promises = this.fileList.map((item, index) => {
        const config = this.fullApply ? this.fileConfigs[this.currentFileIndex] : this.fileConfigs[index]
        return this.handleSingleFile(item, config, index)
      })

      try {
        await Promise.all(promises)
      } catch (error) {
        console.error('批量处理文件失败:', error)
      }
    },

    // 准备文档数据
    prepareDocumentData(config, fileName) {
      const data = {
        docForm: config.segmentedType === 1 ? 'text_model' : 'hierarchical_model',
        processRule: {
          removeExtraSpaces: config.pretreatment ? config.pretreatment.includes('removeExtraSpaces') : false,
          removeUrlsEmails: config.pretreatment ? config.pretreatment.includes('removeUrlsEmails') : false,
          segmentation: {
            separator: config.separator || '',
            maxTokens: config.maxTokens || 0,
            chunkOverlap: config.chunkOverlap || 0
          }
        }
      }

      if (fileName) {
        data.name = fileName
        data.datasetId = this.$route.query.id || ''
      }

      // 父子分段
      if (config.segmentedType === 2) {
        if (config.fzfdType === 1) {
          data.processRule.parentMode = 'paragraph'
          data.processRule.subchunkSegmentation = {
            separator: config.subSeparator || '',
            maxTokens: config.subMaxTokens || 0,
            chunkOverlap: config.subChunkOverlap || 0
          }
        } else {
          data.processRule.parentMode = 'full-doc'
        }
      }

      return data
    }
  },
  // 监听props变化，当fileList变化时重新初始化列表
  watch: {
    fileList: {
      handler() {
        this.initFileList()
      },
      deep: true
    }
  },
  // 组件销毁时清除定时器
  beforeDestroy() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer)
      this.progressTimer = null
    }
  }
}
</script>

<style lang="less" scoped>
.content {
  .file-list {
    .file-item {
      border: dashed 1px #d9d9d9;
      padding: 10px 15px;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      margin-bottom: 10px;
      position: relative;
      overflow: hidden;
      border-radius: 5px;

      // 添加进度条背景
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        background: #f4f5f7;
        width: var(--progress, 0%);
        transition: width 0.3s ease;
        z-index: 0;
      }
      &[data-progress='100']::before {
        background: #f5fbf5;
      }
      &[data-progress='100'] {
        border-color: #BEE8D2;
      }

      .file-icon {
        position: relative;
        z-index: 1;
        img {
          width: 20px;
          height: auto;
        }
        margin-right: 15px;
      }
      .file-name {
        position: relative;
        z-index: 1;
        width: 100%;
        font-size: 14px;
        color: #000000;
      }
      .file-size {
        position: relative;
        z-index: 1;
        width: 100px;
        font-size: 12px;
        color: #999;
      }
      // 添加进度显示
      .file-progress {
        position: relative;
        z-index: 1;
        width: 80px;
        font-size: 14px;
        color: #000000;
        text-align: right;
        &::after {
          content: '%';
        }
      }
      position: relative;
      &:hover {
        .file-actions {
          display: block;
        }
      }
      .file-actions {
        display: none;
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        .ant-btn {
          padding: 0 5px;
        }
      }
      .num {
        position: absolute;
        right: 30px;
        top: 19px;
        color: #03b879;
        font-size: 16px;
      }
    }
  }
}
</style>
